<template>
  <div mfa>
    <Layout
      :app="app"
      header="Two Factor Authentication"
      :title="title"
    >
      <div
        v-if="loading"
        class="loading"
      >
        <Spinner />
      </div>
      <template
        v-if="!loading"
        v-slot:desc
      >
        <span v-html="lead" />
      </template>
      <template v-if="!loading">
        <ul
          v-if="errors"
          class="error"
        >
          <li
            v-for="err in errors"
            :key="err.message"
          >
            {{ err.message }}
          </li>
        </ul>
        <template v-if="step === STEPS.verifyPassword">
          <Input
            v-model="password"
            :autofocus="true"
            class="input"
            label="Password"
            type="password"
            placeholder=""
            @enter="verifyPassword"
          />
          <Button
            class="button"
            label="continue"
            @click="verifyPassword"
          />
        </template>
        <template v-if="step === STEPS.selectAction">
          <div v-if="isMfaEnabled && !canMfaBeDisabled">
            Two factor authentication can not be disabled for your account.
          </div>
          <Button
            v-else
            class="button"
            :label="selectActionLabel"
            @click="selectAction"
          />
        </template>
        <template v-if="step === STEPS.verifyToken">
          <img
            :src="qrImageUrl"
            class="image"
          >
          <CodeInput
            v-model="token"
            :autofocus="true"
            label="Input a token from your authentication app"
            size="6"
            @enter="verifyToken"
          />
          <Button
            class="button"
            label="Confirm"
            @click="verifyToken"
          />
        </template>
        <template v-if="step === STEPS.done">
          Done!
        </template>
      </template>
      <div
        v-if="app && !isAdminApp"
        class="links"
      >
        <a
          class="link"
          :href="returnUrl"
        >
          Back to {{ app.name }}
        </a>
        <router-link
          v-if="step === STEPS.verifyPassword"
          class="link"
          :to="`/${appKey}/forgot-password`"
        >
          Forgot your password?
        </router-link>
      </div>
    </Layout>
  </div>
</template>

<script>
  import gql from 'graphql-tag';
  import { ADMIN_APP_KEY } from '@/globals';
  import Layout from './layout.vue';
  import Button from '../../components/button.vue';
  import CodeInput from '../../components/code-input.vue';
  import Input from '../../components/input.vue';
  import Spinner from '../../components/spinner.vue';
  import app from '../../mixins/app';

  const STEPS = {
    verifyPassword: 'verify-password',
    selectAction: 'select-action',
    verifyToken: 'verify-token',
    done: 'done',
  };

  const preloadImage = async (url) => new Promise((resolve) => {
    const image = new Image();
    image.onload = resolve;
    image.onerror = resolve;
    image.src = url;
  });

  export default {
    name: 'Mfa',
    components: {
      Button,
      CodeInput,
      Input,
      Layout,
      Spinner,
    },
    mixins: [app],
    data() {
      return {
        errors: null,
        loading: true,
        me: null,
        password: null,
        qrImageUrl: null,
        step: this.$route.params.token ? STEPS.selectAction : STEPS.verifyPassword,
        STEPS,
        token: null,
      };
    },
    apollo: {
      me: {
        query: gql`
          query {
            me {
              email
              id
              mfa
              name
              group {
                enforcedMfa
              }
            }
          }
        `,
        error() {
          this.$router.push({ name: 'sign-in', params: { returnRoute: '2fa' } });
        },
      },
    },
    computed: {
      canMfaBeDisabled() {
        return this.isMfaEnabled && !this.isMfaEnforced;
      },
      enforceMfa() {
        return this.isMfaEnforced && !this.isMfaEnabled;
      },
      isMfaEnabled() {
        return this.me && this.me.mfa;
      },
      isMfaEnforced() {
        return this.me && this.me.group && this.me.group.enforcedMfa;
      },
      lead() {
        switch (this.step) {
        case STEPS.verifyPassword:
          return 'You need to verify your identity before you continue.';
        case STEPS.selectAction:
          if (this.enforceMfa) {
            return `
              Two factor authentication is mandatory for your account.<br>
              You need to enable it in order to continue.
            `;
          }
          return `Two factor authentication is ${this.isMfaEnabled ? 'enabled' : 'disabled'}.`;
        case STEPS.verifyToken:
          return `
            Please scan this QR code in your authentication app.<br>
            (Authy, Google Authenticator or Microsoft Authenticator.)
          `;
        default:
          return '';
        }
      },
      selectActionLabel() {
        return `${this.isMfaEnabled ? 'disable' : 'enable'} two factor authentication`;
      },
      title() {
        switch (this.step) {
        case STEPS.verifyToken:
          return `${this.isMfaEnabled ? 'Disable' : 'Enable'} two factor authentication`;
        case STEPS.done:
          return `Two factor authentication ${this.isMfaEnabled ? 'enabled' : 'disabled'}!`;
        default:
          return this.me ? `Hello ${this.me.name}!` : '';
        }
      },
      returnUrl() {
        if (this.step === STEPS.done && this.$route.params.return) {
          return this.$route.params.return;
        }
        const path = this.$route.query.return || '/';
        return `${this.app.redirect}${path}`;
      },
      isAdminApp() {
        return this.appKey === ADMIN_APP_KEY;
      },
    },
    watch: {
      me() {
        this.loading = false;
      },
      step(newValue) {
        if (newValue === STEPS.done) {
          setTimeout(() => {
            window.location.href = this.returnUrl;
          }, 1000);
        }
      },
    },
    methods: {
      async performStep(stepFn) {
        this.errors = null;
        this.loading = true;
        try {
          const nextStep = await stepFn(this);
          if (nextStep) {
            this.step = nextStep;
          }
        } catch (error) {
          if (error) {
            this.errors = error.graphQLErrors;
          }
        } finally {
          this.loading = false;
        }
      },
      verifyPassword() {
        this.performStep(async () => {
          await this.$apollo.query({
            query: gql`
            query ($password: String!) {
              verifyPassword (password: $password)
            }`,
            variables: {
              password: this.password,
            },
          });
          return STEPS.selectAction;
        });
      },
      selectAction() {
        this.performStep(async () => {
          if (this.isMfaEnabled) {
            const { data } = await this.$apollo.mutate({
              mutation: gql`
              mutation {
                disableMfa {
                  mfa
                }
              }`,
            });
            Object.assign(this.me, data.disableMfa);
            return STEPS.done;
          }
          const { data: { createMfaSecret } } = await this.$apollo.mutate({
            mutation: gql`
             mutation {
              createMfaSecret {
                qr
              }
            }`,
          });
          await preloadImage(createMfaSecret.qr);
          this.qrImageUrl = createMfaSecret.qr;
          return STEPS.verifyToken;
        });
      },
      verifyToken() {
        this.performStep(async () => {
          const { data } = await this.$apollo.mutate({
            mutation: gql`
            mutation ($token: String!) {
              enableMfa (token: $token) {
                mfa
              }
            }`,
            variables: {
              token: this.token,
            },
          });
          Object.assign(this.me, data.enableMfa);
          return STEPS.done;
        });
      },
    },
  };
</script>

<style scoped lang="stylus">
  @require '~@/style/colors'
  @require '~@/style/type'

  [mfa]
    text-align: center

  .input
    margin-top: 30px
    text-align: left
    &:first-of-type
      margin-top: 0

  .button,
  .loading
    width: 100%
    margin-top: 60px
    &:first-child
      margin-top: 0

  .loading
    height: 50px
    display: flex
    align-items: center
    justify-content: center

  .links
    @extend $type-small
    display: block
    width: 100%
    text-align: center
    margin-top: 30px
    a
      display: inline-block
      margin-left: 10px
      padding-right: 10px
      border-right: 1px solid #aaa
      &:last-of-type
        border-right: none

  .error
    @extend $type-small, $type-medium
    color: #a00
    background-color: rgba(205,92,92,.1)
    text-align: center
    list-style: none
    margin: 0 0 20px 0
    padding: 10px

  .image
    margin-bottom: 1em
</style>
